'use client'

import UIButton from '@components/ui/UIButton'
import UIFlex from '@components/ui/UIFlex'
import UIText from '@components/ui/UIText'
import { Box, Drawer, Radio, RadioGroup, Slider, styled } from '@mui/joy'
import { appInterface, useIsAppControl } from '@store/isApp'
import { useTTSControl } from '@store/tts'
import color from '@ui/style/color.theme'
import zIndexes from '@ui/style/zIndexes.theme'
import pxToRem from '@utils/pxToRem'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { useRecoilValue } from 'recoil'
import useSetRefreshValue from '@hooks/Interface/useSetRefreshValue'

const CloseBtn = styled('button')({
	display: 'flex',
	justifyContent: 'center',
	width: '100%',
	paddingTop: pxToRem(14),
	paddingBottom: pxToRem(14),
	'&::before': {
		content: '""',
		display: 'block',
		width: pxToRem(42),
		height: pxToRem(4),
		background: color.colGray3,
		borderRadius: pxToRem(2),
	},
})

const SettingTitle = styled(UIText)({
	fontSize: pxToRem(16),
	lineHeight: pxToRem(24),
	fontWeight: 600,
	color: color.colBlack,
})

interface TTSDrawerProps {
	text: string
}

const TTSDrawer = ({ text }: TTSDrawerProps) => {
	const nativeEvent = useRecoilValue(appInterface)
	const { setRefreshValue } = useSetRefreshValue()
	const { appInfo } = useIsAppControl()
	const { isTTSDraw, setIsTTSDraw, setIsTTSPlaying } = useTTSControl()
	const [gender, setGender] = useState<TTSGender>('W')
	const [speed, setSpeed] = useState<TTSSpeed>('1.0')
	const isIOS = appInfo?.DType === 'I'
	const marks = [
		{
			value: 0,
			label: '느림',
		},
		{
			value: 1,
			label: '보통',
		},
		{
			value: 2,
			label: '빠름',
		},
	]
	const onClose = () => {
		setIsTTSDraw(false)
	}
	const sendEnterBackground = ((event: CustomEvent) => {
		console.log('sendEnterBackground TabBar drawer', event)
		setIsTTSPlaying(false)
		// onClose()
	}) as EventListener
	const handleGender = (event: ChangeEvent<HTMLInputElement>) => {
		setGender(event.target.value as TTSGender)
	}
	const handleSpeed = (event: Event, value: number | number[]) => {
		setSpeed(`${((value as number) * 0.5 + 0.5).toFixed(1)}` as TTSSpeed)
	}
	const handleTTSStart = useCallback(() => {
		const content = document.createElement('div')
		content.innerHTML = text
		nativeEvent({
			key: 'startTTS',
			value: {
				gender,
				speed,
				text: content.innerText,
			},
		})
		setIsTTSPlaying(true)
		onClose()
	}, [nativeEvent, gender, speed, text])
	useEffect(() => {
		if (isTTSDraw) {
			setRefreshValue(false)
		}
		window.addEventListener('sendEnterBackground', sendEnterBackground)
		return () => {
			setRefreshValue(true)
			window.removeEventListener('sendEnterBackground', sendEnterBackground)
		}
	}, [isTTSDraw])
	return (
		<Drawer
			open={isTTSDraw}
			onClose={onClose}
			anchor="bottom"
			sx={{
				zIndex: zIndexes.fullModal,
				'.MuiDrawer-content': {
					width: '100%',
					display: 'flex',
					height: 'auto',
					maxHeight: '80vh',
					borderRadius: `${pxToRem(16)} ${pxToRem(16)} 0 0`,
					borderTop: `1px solid ${color.colGray3}`,
					overflow: 'hidden',
					transition: 'var(--Drawer-transitionDuration) var(--Drawer-transitionFunction)',
					background: color.colWhite,
				},
				'.MuiDrawer-backdrop': {
					backdropFilter: 'none',
					background: 'rgba(0,0,0,0.7)',
				},
			}}
		>
			<UIFlex height="100%" overflow="hidden">
				<CloseBtn
					type="button"
					onClick={onClose}
					sx={{
						flexShrink: 0,
					}}
				>
					<UIText readonly>닫기 버튼</UIText>
				</CloseBtn>
				<Box flexGrow={1} overflow="auto" px={pxToRem(20)}>
					<UIText
						textAlign="center"
						fontSize={pxToRem(18)}
						lineHeight={pxToRem(24)}
						fontWeight={700}
						pb={pxToRem(16)}
						mb={pxToRem(16)}
						borderBottom={`1px solid ${color.colGray3}`}
					>
						본문 듣기 설정
					</UIText>
					<UIFlex sx={{ '&> *:not(:first-child)': { marginTop: pxToRem(36) } }}>
						{!isIOS && (
							<Box>
								<SettingTitle>성별</SettingTitle>
								<RadioGroup
									name="radio-gender"
									sx={{
										mt: pxToRem(12),
									}}
								>
									<UIFlex flexDirection="row">
										<Radio
											value="W"
											label={
												<UIText
													fontSize={pxToRem(15)}
													lineHeight={pxToRem(22)}
												>
													여성
												</UIText>
											}
											disableIcon
											slotProps={{
												action: {
													sx: {
														top: 0,
														left: 0,
														right: 0,
														bottom: 0,
														border: 0,
														opacity: 0,
													},
												},
												label: ({ checked }) => ({
													sx: {
														display: 'flex',
														width: '100%',
														height: pxToRem(48),
														borderRadius: `${pxToRem(8)} 0 0 ${pxToRem(
															8,
														)}`,
														border: `1px solid ${
															checked
																? color.colPointBlue
																: color.colGray3
														}`,
														borderRight: checked
															? `1px solid ${color.colPointBlue}`
															: 0,
														background: checked
															? color.colLightBlue
															: color.colWhite,
														flexGrow: 1,
														alignItems: 'center',
														justifyContent: 'center',
														span: {
															color: checked
																? color.colPointBlue
																: color.colGray5,
															fontWeight: checked ? 600 : 500,
														},
													},
												}),
											}}
											sx={{
												width: '50%',
											}}
											checked={gender === 'W'}
											onChange={handleGender}
										/>

										<Radio
											value="M"
											label={
												<UIText
													fontSize={pxToRem(15)}
													lineHeight={pxToRem(22)}
												>
													남성
												</UIText>
											}
											disableIcon
											slotProps={{
												action: {
													sx: {
														top: 0,
														left: 0,
														right: 0,
														bottom: 0,
														border: 0,
														opacity: 0,
													},
												},
												label: ({ checked }) => ({
													sx: {
														display: 'flex',
														width: '100%',
														height: pxToRem(48),
														borderRadius: `0 ${pxToRem(8)} ${pxToRem(
															8,
														)} 0`,
														border: `1px solid ${
															checked
																? color.colPointBlue
																: color.colGray3
														}`,
														borderLeft: checked
															? `1px solid ${color.colPointBlue}`
															: 0,
														background: checked
															? color.colLightBlue
															: color.colWhite,
														flexGrow: 1,
														alignItems: 'center',
														justifyContent: 'center',
														span: {
															color: checked
																? color.colPointBlue
																: color.colGray5,
															fontWeight: checked ? 600 : 500,
														},
													},
												}),
											}}
											sx={{
												width: '50%',
											}}
											checked={gender === 'M'}
											onChange={handleGender}
										/>
									</UIFlex>
								</RadioGroup>
							</Box>
						)}
						<Box pb={pxToRem(20)}>
							<SettingTitle>말하기 속도</SettingTitle>
							<Slider
								min={0}
								max={2}
								defaultValue={1}
								step={1}
								marks={marks}
								track={false}
								slotProps={{
									markLabel: {
										component: 'div',
									},
								}}
								sx={{
									'&.MuiSlider-root': {
										paddingTop: `${pxToRem(16)}!important`,
										paddingBottom: `${pxToRem(16)}!important`,
									},
									'& .MuiSlider': {
										'&-rail': {
											borderRadius: pxToRem(3),
											background: color.colGray2,
										},
										'&-track': {
											background: color.colPointBlue,
											display: 'block',
										},
										'&-mark': {
											display: 'none',
										},
										'&-markLabel': {
											fontSize: pxToRem(14),
											lineHeight: pxToRem(20),
											fontWeight: 500,
											color: color.colGray5,
											top: pxToRem(38),
											'&:first-of-type': {
												transform: 'none',
											},
											'&:last-of-type': {
												transform: 'translateX(-100%)',
											},
										},
										'&-markLabelActive': {
											color: color.colPointBlue,
											fontWeight: 600,
										},
										'&-thumb': {
											width: pxToRem(24),
											height: pxToRem(24),
											borderRadius: '9999px',
											filter: 'drop-shadow(0px 3px 4px rgba(0, 0, 0, 0.10))',
											transform: 'translateY(-50%)',
											'&[style*="left: 50%"]': {
												transform: 'translate(-50%, -50%)',
											},
											'&[style*="left: 100%"]': {
												transform: 'translate(-100%, -50%)',
											},
											'&::before': {
												border: `1px solid ${color.colGray3}`,
											},
											'&:focus-visible, &.Mui-focusVisible': {
												outline: 0,
												outlineWidth: 0,
												outlineColor: 'transparent',
											},
										},
									},
								}}
								onChange={handleSpeed}
							/>
						</Box>
						<UIButton
							size="xl"
							sx={{
								lineHeight: pxToRem(20),
							}}
							onClick={handleTTSStart}
						>
							본문 듣기 시작
						</UIButton>
					</UIFlex>
				</Box>
			</UIFlex>
		</Drawer>
	)
}

export default TTSDrawer
